// 🔑 `import type` trennt den Kreis an der Wurzel: Die DTO-Schicht darf die Tabellenschicht zur Laufzeit
// nicht laden, weil diese zurueck auf search-table.dto.res zeigt - und dort erbt SearchFilterResponse von
// SearchFilterRequest aus dieser Datei. Beide Namen sind Schnittstellen und werden nur als Typ gebraucht.
import type {SearchTableDefinition, SearchTableFilterDefinition} from "../../../libs/table/items/search-table.definition";
import {SearchSettings} from "../../../libs/table/items/search.settings";
import {isDefined, isFalse, isTrue} from "../../../libs/utils/epic.utils";

export class SearchRequest {

  public static readonly DEFAULT_PAGE_SIZE: number = 20;
  public static readonly DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'asc';

  public query: string = '';
  public page: number = 1; // Page number
  public pageSize: number = SearchRequest.DEFAULT_PAGE_SIZE; // Number of Documents per page
  public sortField?: string;
  public sortOrder?: 'asc' | 'desc'; // asc or desc
  public fields?: string[];
  public filters: SearchFilterRequest[] = [];

  constructor(init?: Partial<SearchRequest>) {
    Object.assign(this, init);
    if (init?.filters) {
      this.filters = init.filters.map((f) => new SearchFilterRequest(f));
      delete init.filters;
    }
  }

  public static ofDefinition(definition: SearchTableDefinition): SearchRequest {
    return new SearchRequest({
      sortField: definition.defaultSortField,
      sortOrder: definition.defaultSortOrder,
      fields: definition.sourceFields,
      filters: definition.filters
    });
  }

  public addFilters(filters: SearchFilterRequest[] | undefined): void {
    filters?.forEach((filter) => this.addFilter(filter));
  }

  public addFilter(filter: SearchFilterRequest): void {
    const index = this.filters.findIndex((f) => f.pos === filter.pos);
    if (index === -1) {
      this.filters.push(filter);
    } else {
      this.filters[index] = filter;
    }
  }

  public updateSettings(settings: SearchSettings | undefined, definition: SearchTableDefinition): void {
    if (isDefined(settings)) {
      if (isDefined(settings.pageSize)) {
        this.pageSize = settings.pageSize;
      }
      const forceDefaultSort = isTrue(definition.forceDefaultSort);
      if (isFalse(forceDefaultSort) && isDefined(settings.sortField) && isDefined(settings.sortOrder)) {
        this.sortField = settings.sortField;
        this.sortOrder = settings.sortOrder;
      }
    }
  }
}

export class SearchFilterRequest implements SearchTableFilterDefinition {

  public id?: string;
  public pos!: number; // Visible: 0 <= pos <= 1000 / see SearchTableComponent.getFilters
  public filterValueField!: string;
  public filterIdField?: string;
  public title?: string;
  public selected?: string[];
  public range?: SearchFilterRangeRequest;

  constructor(init?: Partial<SearchFilterRequest>) {
    Object.assign(this, init);
  }
}

export class SearchFilterRangeRequest {

  public from?: Date;
  public to?: Date;

  private constructor(range?: { from?: Date; to?: Date }) {
    if (range) {
      this.from = range.from;
      this.to = range.to;
    }
  }

  public static empty(): SearchFilterRangeRequest {
    return new SearchFilterRangeRequest();
  }

  public static from(from: Date | string): SearchFilterRangeRequest {
    return new SearchFilterRangeRequest({from: this.parseDate(from)});
  }

  public static to(to: Date | string): SearchFilterRangeRequest {
    return new SearchFilterRangeRequest({to: this.parseDate(to)});
  }

  public static fromTo(from: Date | string, to: Date | string): SearchFilterRangeRequest {
    return new SearchFilterRangeRequest({from: this.parseDate(from), to: this.parseDate(to)});
  }

  private static parseDate(input: Date | string): Date {
    if (input instanceof Date) {
      return input;
    }
    const parsed = new Date(input);
    if (isNaN(parsed.getTime())) {
      throw new Error(`Invalid date format: ${input}. Expected yyyy-mm-dd or Date object`);
    }
    return parsed;
  }
}
